import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import { Shield, Lock, CreditCard } from 'lucide-react';
import { PaymentLogosBar, fetchGateways, AzulBadge, CardnetBadge, StripeBadge, KlarnaBadge, VisaLogo, MastercardLogo } from '../components/PaymentBadges';
import Navbar from "../components/Navbar";
import LoadingScreen from "../components/LoadingScreen";
import ErrorScreen from "../components/ErrorScreen";
import { useAuth } from '../context/AuthContext';
import { formatDOP } from '../lib/currency';
import { validateDocument } from '../lib/documents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Checkout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Descargo de responsabilidad — obligatorio antes de pagar. El backend lo
  // exige y guarda el timestamp de aceptación como evidencia legal.
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Pasarela: azul (RD$) por defecto. La lista completa la publica el backend
  // en /payments/gateways y se renderiza dinámicamente (Cardnet, Portal, etc.
  // se activan sin tocar este archivo).
  const [gateway, setGateway] = useState('azul');
  const [gateways, setGateways] = useState([{ id: 'azul' }]);
  // Tasa DOP→USD vigente (la publica el backend con la lista de pasarelas):
  // permite mostrar el equivalente en US$ que Stripe cobrará realmente.
  const [dopPerUsd, setDopPerUsd] = useState(null);
  // Reserva ya creada en esta sesión de checkout: se reutiliza al reintentar
  // el pago para no crear una segunda reserva que choque con la primera.
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    document: '',
    insurance: 'none',
    deliveryRequested: false,
    deliveryAddress: ''
  });

  useEffect(() => {
    fetchGateways().then(data => {
      const list = data.gateways || [{ id: 'azul' }];
      setGateways(list);
      const stripeGw = list.find(g => g.id === 'stripe');
      if (stripeGw && stripeGw.dop_per_usd > 0) setDopPerUsd(stripeGw.dop_per_usd);
    });
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        phone: user.phone_whatsapp || prev.phone,
        document: user.document_id || prev.document,
      }));
    }
  }, [user]);

  const bookingData = JSON.parse(localStorage.getItem('bookingData') || '{}');
  if (!bookingData.asset) {
    navigate('/');
    return null;
  }

  const documentCheck = validateDocument(formData.document);

  const handleSubmit = async () => {
    // El botón ya está deshabilitado si faltan términos/campos; guardas defensivas.
    if (step === 1 && !documentCheck.valid) return;
    if (step === 2 && (!termsAccepted || !waiverAccepted || (formData.deliveryRequested && !formData.deliveryAddress.trim()))) return;
    if (step < 3) {
      setStep(step + 1);
      return;
    }

    setPayError(null);
    setSessionExpired(false);
    setProcessing(true);
    try {
      // Reutiliza la reserva creada en un intento previo; solo crea una nueva
      // si aún no existe (evita el auto-conflicto "ya reservado" al reintentar).
      let bookingId = createdBookingId;
      if (!bookingId) {
        const bookingRes = await fetch(`${BACKEND_URL}/api/bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            asset_id: bookingData.asset.asset_id,
            start_datetime: `${bookingData.startDate}T${bookingData.startTime}:00Z`,
            end_hours: bookingData.hours,
            insurance_type: formData.insurance,
            client_name: formData.name,
            client_email: formData.email,
            client_phone: formData.phone,
            client_document: formData.document,
            liability_waiver_accepted: waiverAccepted,
            delivery_requested: formData.deliveryRequested,
            delivery_address: formData.deliveryRequested ? formData.deliveryAddress : null,
          })
        });

        const booking = await bookingRes.json();
        if (!bookingRes.ok) {
          setProcessing(false);
          if (bookingRes.status === 401) {
            setSessionExpired(true);
            setPayError(t('checkout.sessionExpiredError'));
          } else {
            setPayError(booking.detail || t('checkout.createBookingError'));
          }
          return;
        }
        bookingId = booking.booking_id;
        setCreatedBookingId(bookingId);

        // Guarda WhatsApp y cédula en el perfil para autocompletar en futuras
        // reservas (fire-and-forget; no bloquea el pago).
        if ((formData.phone && formData.phone !== user?.phone_whatsapp) ||
            (formData.document && formData.document !== user?.document_id)) {
          fetch(`${BACKEND_URL}/api/auth/me`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ phone_whatsapp: formData.phone, document_id: formData.document }),
          }).catch(() => {});
        }
      }

      const appBase = window.location.origin + (process.env.PUBLIC_URL || '');
      const azulLocale = i18n.language?.startsWith('en') ? 'EN' : 'ES';
      const checkoutRes = await fetch(`${BACKEND_URL}/api/payments/checkout?booking_id=${bookingId}&origin_url=${encodeURIComponent(appBase)}&locale=${azulLocale}&gateway=${gateway}`, {
        method: 'POST',
        credentials: 'include'
      });

      const checkout = await checkoutRes.json();
      if (!checkoutRes.ok || !checkout.url) {
        setProcessing(false);
        if (checkoutRes.status === 401) {
          setSessionExpired(true);
          setPayError(t('checkout.sessionExpiredError'));
        } else {
          setPayError(checkout.detail || t('checkout.startPaymentError'));
        }
        return;
      }
      // La reserva ya se creó en el backend; los datos temporales del carrito
      // en localStorage ya no son necesarios y ensucian sesiones futuras si
      // el cliente vuelve al home después de pagar.
      localStorage.removeItem('bookingData');
      // Mantenemos la pantalla de carga hasta que el navegador cambie de página.
      if (checkout.fields) {
        // Azul Payment Page: enviamos los campos firmados por POST a la página de Azul.
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = checkout.url;
        Object.entries(checkout.fields).forEach(([k, v]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = k;
          input.value = v;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      } else {
        // Pasarela tipo redirección (respuesta {url} sin campos): redirigimos.
        window.location.href = checkout.url;
      }
    } catch (error) {
      console.error('Error:', error);
      setProcessing(false);
      setPayError(t('checkout.processBookingError'));
    }
  };

  const basePrice = bookingData.totalPrice || 0;
  const insurancePrice = formData.insurance === 'standard'
    ? Math.round(basePrice * 0.05)
    : formData.insurance === 'premium'
    ? Math.round(basePrice * 0.08)
    : 0;

  // Tasa real del proveedor (viaja con el asset desde la ficha); 0.15 como
  // respaldo. Mantiene el desglose igual a lo que el backend cobrará.
  const commissionRate = bookingData.asset?.commission_rate ?? 0.15;
  const commission = Math.round(basePrice * commissionRate);
  const finalTotal = basePrice + insurancePrice + commission;

  // Proceso de pago en curso (creando reserva + sesión de Stripe / redirigiendo).
  if (processing) {
    return <LoadingScreen message={t('checkout.processingPayment', 'Procesando tu pago de forma segura...')} />;
  }

  // El pago/reserva falló: en vez de un alert o pantalla en blanco, mostramos
  // una pantalla de error que permite reintentar o volver al paso anterior.
  if (payError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <ErrorScreen
          inline
          className="min-h-[70vh]"
          title={t('checkout.paymentErrorTitle', 'No se pudo completar el pago')}
          message={payError}
          onRetry={sessionExpired ? undefined : handleSubmit}
          onBack={sessionExpired
            ? () => navigate('/login')
            : () => { setPayError(null); setStep(2); }}
          backLabel={sessionExpired
            ? t('checkout.goToLogin', 'Iniciar sesión')
            : t('checkout.backToPreviousStep', 'Volver al paso anterior')}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-[#134168] font-medium transition-colors min-h-[48px] active:opacity-75"
        >
          {t('checkout.backToAsset')}
        </button>

        <div className="overflow-x-auto whitespace-nowrap mb-6 -mx-4 px-4 pb-2 md:overflow-visible md:whitespace-normal">
          <div className="inline-flex md:flex gap-2">
            {[t('checkout.stepPersonal'), t('checkout.stepInsurance'), t('checkout.stepPayment')].map((label, i) => (
              <span
                key={i}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${
                  step === i + 1
                    ? 'bg-[#134168] text-white border-[#134168]'
                    : step > i + 1
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200'
                }`}
              >
                <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs font-bold"
                  style={{ borderColor: step >= i + 1 ? 'white' : '#D1D5DB', color: step >= i + 1 ? 'white' : '#9CA3AF' }}
                >{i + 1}</span>
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 shadow-sm"
            >
              {step === 1 && (
                <div>
                  <h2 className="text-3xl font-heading font-bold mb-6">{t('checkout.personalInfo')}</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('checkout.fullName')}</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                        placeholder={t('checkout.namePlaceholderEx')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('checkout.email')}</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                        placeholder={t('checkout.emailPlaceholderEx')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('checkout.whatsapp')}</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                        placeholder="+1-809-555-0100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('checkout.idDocument')}</label>
                      <input
                        type="text"
                        value={formData.document}
                        onChange={(e) => setFormData({...formData, document: e.target.value})}
                        aria-invalid={!!formData.document && !documentCheck.valid}
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${
                          formData.document && !documentCheck.valid
                            ? 'border-red-300 focus:ring-red-400'
                            : 'border-gray-200 focus:ring-[#134168]'
                        }`}
                        placeholder="001-1234567-8"
                      />
                      {formData.document && !documentCheck.valid && (
                        <p className="mt-1.5 text-xs text-red-600">{t('checkout.invalidDocument')}</p>
                      )}
                      {formData.document && documentCheck.valid && (
                        <p className="mt-1.5 text-xs text-green-600">
                          {documentCheck.type === 'cedula' ? t('checkout.validCedula') : t('checkout.validPassport')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="text-3xl font-heading font-bold mb-6">{t('checkout.insuranceOptionalTitle')}</h2>
                  <div className="space-y-4">
                    {[
                      { value: 'none', title: t('checkout.insuranceNone'), price: 0, desc: t('checkout.insuranceNoneDesc') },
                      { value: 'standard', title: t('checkout.insuranceStandard'), price: Math.round(bookingData.totalPrice * 0.05), desc: t('checkout.insuranceStandardDesc') },
                      { value: 'premium', title: t('checkout.insurancePremium'), price: Math.round(bookingData.totalPrice * 0.08), desc: t('checkout.insurancePremiumDesc') }
                    ].map(ins => (
                      <div
                        key={ins.value}
                        onClick={() => { setFormData({...formData, insurance: ins.value}); setCreatedBookingId(null); }}
                        className={`border-2 rounded-2xl p-6 cursor-pointer transition-all ${
                          formData.insurance === ins.value
                            ? 'border-[#134168] bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-bold text-lg mb-1">{ins.title}</h3>
                            <p className="text-sm text-gray-600">{ins.desc}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-xl">{formatDOP(ins.price)}</p>
                            {ins.price > 0 && <p className="text-xs text-gray-500">{t('checkout.extra')}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 bg-gray-50 border border-gray-200 rounded-2xl p-6">
                    <h3 className="font-bold text-lg mb-3">{t('checkout.deliveryTitle', 'Entrega a Domicilio')}</h3>
                    <label className="flex items-start gap-3 cursor-pointer mb-4">
                      <input
                        type="checkbox"
                        checked={formData.deliveryRequested}
                        onChange={e => setFormData({...formData, deliveryRequested: e.target.checked})}
                        className="mt-1 w-5 h-5 accent-[#134168] shrink-0"
                      />
                      <span className="text-sm text-gray-700">
                        {t('checkout.deliveryAccept', 'Sí, quiero que entreguen el vehículo en mi ubicación.')}
                      </span>
                    </label>
                    {formData.deliveryRequested && (
                      <div className="mt-2">
                        <label className="block text-sm font-medium mb-2">{t('checkout.deliveryAddress', 'Dirección de Entrega')}</label>
                        <textarea
                          value={formData.deliveryAddress}
                          onChange={(e) => setFormData({...formData, deliveryAddress: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                          placeholder={t('checkout.deliveryAddressPlaceholder', 'Indique la calle, número, sector y referencias...')}
                          rows={3}
                        />
                      </div>
                    )}
                  </div>

                  <div className="mt-8 bg-gray-50 border border-gray-200 rounded-2xl p-6">
                    <h3 className="font-bold text-lg mb-3">{t('checkout.termsBlockTitle')}</h3>
                    <div className="text-sm text-gray-600 space-y-2 max-h-40 overflow-y-auto mb-4 pr-2">
                      <p><Trans i18nKey="checkout.term1" /></p>
                      <p><Trans i18nKey="checkout.term2" /></p>
                      <p><Trans i18nKey="checkout.term3" /></p>
                      <p><Trans i18nKey="checkout.term4" /></p>
                      <p><Trans i18nKey="checkout.term5" /></p>
                      <p><Trans i18nKey="checkout.term6" /></p>
                      <p><Trans i18nKey="checkout.term7" /></p>
                      <p><Trans i18nKey="checkout.term8" /></p>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={e => setTermsAccepted(e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#134168] shrink-0"
                      />
                      <span className="text-sm text-gray-700">
                        <Trans i18nKey="checkout.termsAcceptLong" />
                      </span>
                    </label>
                  </div>

                  {/* Hoja de descargo — obligatoria, evidencia legal ante
                      accidentes. Se persiste con timestamp en la reserva. */}
                  <div className="mt-4 border border-amber-200 bg-amber-50 rounded-2xl p-6">
                    <h3 className="font-bold text-lg mb-2 text-amber-900">{t('checkout.waiverTitle', 'Hoja de descargo de responsabilidad')}</h3>
                    <p className="text-sm text-amber-900 mb-4">
                      {t('checkout.waiverBody', 'Confirmo que asumo la responsabilidad total por daños al vehículo alquilado, por accidentes durante mi uso, y por infracciones cometidas durante el alquiler. RedMobility actúa como intermediario y no es responsable por lesiones a terceros ni daños a bienes durante el uso del vehículo.')}
                    </p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={waiverAccepted}
                        onChange={e => setWaiverAccepted(e.target.checked)}
                        className="mt-1 w-5 h-5 accent-amber-700 shrink-0"
                      />
                      <span className="text-sm font-medium text-amber-900">
                        {t('checkout.waiverAccept', 'Acepto la hoja de descargo de responsabilidad.')}
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="text-3xl font-heading font-bold mb-6">{t('checkout.paymentMethodTitle')}</h2>

                  {/* Selector de pasarela: solo aparece si hay más de una activa.
                      Los títulos/descripciones vienen de las claves i18n
                      `checkout.gw{Id}Title` / `checkout.gw{Id}Desc`, por lo que
                      añadir una pasarela nueva NO requiere tocar este archivo. */}
                  {gateways.length > 1 ? (
                    <div className="space-y-3 mb-6">
                      {gateways.map(gw => {
                        const cap = gw.id.charAt(0).toUpperCase() + gw.id.slice(1);
                        return (
                          <button
                            key={gw.id}
                            type="button"
                            onClick={() => setGateway(gw.id)}
                            className={`w-full flex items-center justify-between p-5 rounded-2xl border-2 text-left transition-all ${
                              gateway === gw.id ? 'border-[#134168] bg-blue-50/70 shadow-sm' : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                                gateway === gw.id ? 'border-[#134168]' : 'border-gray-300'
                              }`}>
                                {gateway === gw.id && <span className="w-2.5 h-2.5 rounded-full bg-[#134168]" />}
                              </span>
                              <div>
                                <p className="font-bold text-gray-900">
                                  {gw.id === 'azul' && 'Tarjeta de Crédito / Débito (Azul BPD)'}
                                  {gw.id === 'cardnet' && 'Tarjeta de Crédito / Débito (Cardnet)'}
                                  {gw.id === 'stripe' && 'Tarjeta Internacional / Klarna'}
                                  {!['azul', 'cardnet', 'stripe'].includes(gw.id) && (gw.label || cap)}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {gw.id === 'azul' && 'Procesamiento seguro en RD$ vía Banco Popular'}
                                  {gw.id === 'cardnet' && 'Procesamiento seguro en RD$ vía Banreservas / BHD'}
                                  {gw.id === 'stripe' && 'Tarjetas internacionales y pago en cuotas con Klarna'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0 pl-2">
                              {gw.id === 'azul' && <AzulBadge className="h-6" />}
                              {gw.id === 'cardnet' && <CardnetBadge className="h-6" />}
                              {gw.id === 'stripe' && (
                                <>
                                  <StripeBadge className="h-6" />
                                  <KlarnaBadge className="h-6" />
                                </>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-6 border-2 border-[#134168] rounded-2xl bg-blue-50 mb-6">
                      <Lock className="w-6 h-6 text-[#134168]" />
                      <div>
                        <p className="font-bold">{t('checkout.secureAzulTitle')}</p>
                        <p className="text-sm text-gray-600">{t('checkout.secureAzulSubtitle')}</p>
                      </div>
                    </div>
                  )}

                  {/* Tarjetas aceptadas */}
                  <div className="border border-gray-200 rounded-2xl p-5 mb-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">{t('checkout.acceptedCards')}</p>
                    <PaymentLogosBar show3DS={false} />
                  </div>

                  {/* Autenticación 3D Secure */}
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex gap-3 items-start">
                    <Shield className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-green-800 mb-1">{t('checkout.threeDSLabel')}</p>
                      <p className="text-xs text-green-700 mb-3">{t('checkout.threeDSDesc')}</p>
                      <div className="flex flex-wrap gap-3 items-center">
                        <PaymentLogosBar show3DS={true} className="justify-start" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={
                  (step === 1 && (!formData.name || !formData.email || !formData.phone || !documentCheck.valid)) ||
                  (step === 2 && (!termsAccepted || !waiverAccepted))
                }
                className="w-full bg-[#134168] text-white py-4 rounded-full font-bold text-lg hover:bg-[#39b4aa] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all mt-8"
              >
                {step === 3 ? t('checkout.proceedToPay') : t('checkout.continueShort')}
              </button>
            </motion.div>
          </div>

          <div className="lg:col-span-1">
            <button
              id="checkout-summary-toggle"
              className="md:hidden w-full flex items-center justify-between bg-white rounded-2xl p-4 shadow-sm mb-0 active:opacity-75"
              onClick={() => setSummaryOpen(!summaryOpen)}
            >
              <span className="font-bold text-gray-900">{t('checkout.bookingSummary')}</span>
              <span className="text-gray-500 text-sm">{summaryOpen ? t('checkout.hideDetails') : t('checkout.viewDetails')}</span>
            </button>

            <div className={`bg-white rounded-b-3xl md:rounded-3xl p-6 shadow-sm lg:sticky lg:top-24 ${
              summaryOpen ? 'block' : 'hidden md:block'
            }`}>
              <h3 className="font-bold text-xl mb-4 hidden md:block">{t('checkout.bookingSummary')}</h3>
              <div className="flex gap-4 mb-4 pb-4 border-b">
                <img src={bookingData.asset.images?.[0]?.url || `${process.env.PUBLIC_URL}/images/placeholder.jpg`} alt={bookingData.asset.name} className="w-20 h-20 rounded-xl object-cover" />
                <div>
                  <p className="font-bold">{bookingData.asset.name}</p>
                  <p className="text-sm text-gray-500">{bookingData.asset.type.replace('_', ' ')}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('checkout.date')}</span>
                  <span className="font-medium">{bookingData.startDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('bookingConfirmation.duration')}</span>
                  <span className="font-medium">
                    {bookingData.hours >= 24 && bookingData.hours % 24 === 0
                      ? `${bookingData.hours / 24} ${bookingData.hours / 24 === 1 ? t('listing.day', 'día') : t('listing.days', 'días')}`
                      : `${bookingData.hours}h`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('common.subtotal')}</span>
                  <span className="font-medium">{formatDOP(bookingData.totalPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('checkout.commission')}</span>
                  <span className="font-medium">{formatDOP(commission)}</span>
                </div>
                {insurancePrice > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('checkout.insurance')}</span>
                    <span className="font-medium">{formatDOP(insurancePrice)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>{t('common.total')}</span>
                  <span className="text-[#134168]">{formatDOP(finalTotal)}</span>
                </div>
                {gateway === 'stripe' && dopPerUsd && (
                  <p className="text-xs text-gray-500 text-right">
                    {t('checkout.usdApprox', {
                      usd: (finalTotal / dopPerUsd).toFixed(2),
                      rate: dopPerUsd.toFixed(2),
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
